import React, { useState, useCallback } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import { ZoomOut } from 'lucide-react';
import { buildDayBoundaryAnnotations } from './chartAnnotations';
import { formatDateLocale } from '../utils/formatters';
import useChartZoomWindow from './useChartZoomWindow';
import AnalyzeZoomButton from './AnalyzeZoomButton';
import {
  INITIAL_VISIBILITY, seriesColors, chartColors, buildCombinedSeries, buildCombinedData, buildCombinedOptions,
} from './combinedChartOptions';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, zoomPlugin, annotationPlugin);

/**
 * CombinedChart — Graphique combiné PM + Température + Humidité (+ CO₂ quand
 * la session en a, D253 lot 3 : pastille dans la légende, axe propre, mention
 * dans le texte d'aide ; rien de tout cela sans CO2).
 *
 * Axes, séries et options Chart.js dans combinedChartOptions.js ; ce
 * composant rend le cadre, la légende cliquable (toggle de chaque courbe),
 * l'annotation rapide et la légende des seuils.
 * Zoom molette/pinch, panoramique sans touche + bouton reset (useChartZoomWindow).
 */
function CombinedChart({ readings, darkMode = false, onQuickAnnotate, onAnalyzeZoom }) {
  const { t } = useTranslation();
  // Panoramique sans touche modificatrice, comportement d'origine conservé.
  const zoom = useChartZoomWindow({ readings, onAnalyzeZoom });
  const chartRef = zoom.chartRef;
  const colors = seriesColors(darkMode);
  const { series, thresholdLines, withCo2 } = buildCombinedSeries(readings, t);
  const [quickAnnot, setQuickAnnot] = useState(null);

  const handleChartClick = useCallback((event) => {
    if (!onQuickAnnotate || !chartRef.current) return;
    const chart = chartRef.current;
    const rect  = chart.canvas.getBoundingClientRect();
    const xPos  = event.clientX - rect.left;
    const yPos  = event.clientY - rect.top;
    const elements = chart.getElementsAtEventForMode(event.nativeEvent || event,
      'nearest', { intersect: false }, false);
    if (!elements.length) { setQuickAnnot(null); return; }
    const idx = elements[0].index;
    const reading = readings[idx];
    if (!reading) return;
    setQuickAnnot({ x: xPos, y: Math.max(40, yPos - 60), timestamp: reading.timestamp });
  }, [onQuickAnnotate, readings, chartRef]);

  // État de visibilité de chaque série
  const [visible, setVisible] = useState(INITIAL_VISIBILITY);

  const toggle = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));

  if (!readings?.length) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: darkMode ? '#94A3B8' : '#4A5568' }}>
        {t('charts.empty.generic')}
      </div>
    );
  }

  const c = chartColors(darkMode);
  const data = buildCombinedData({ readings, series, thresholdLines, colors, visible });
  const dayAnnotations = buildDayBoundaryAnnotations(readings, darkMode)
    .reduce((obj, ann, i) => { obj[`day${i}`] = ann; return obj; }, {});
  const options = buildCombinedOptions({
    readings, series, darkMode, annotations: dayAnnotations, zoom: zoom.zoomOptions, t, withCo2,
  });

  return (
    <div style={{ background: c.card, borderRadius: '12px', border: `1px solid ${c.border}`, padding: '20px', marginBottom: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: c.text, fontFamily: "'Poppins', sans-serif" }}>
          {t('charts.combined.title')}
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
        <AnalyzeZoomButton zoom={zoom} />
        <button
          type="button" onClick={zoom.resetZoom}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px',
            border: `1px solid ${c.border}`, background: 'transparent', color: c.textSec,
            cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' }}
        >
          <ZoomOut size={13} /> {t('common.zoom.reset')}
        </button>
        </div>
      </div>

      {/* Légende custom avec toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        {series.map(({ key, label, unit }) => {
          const col = colors[key].border;
          const active = visible[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
                border: `1px solid ${active ? col : c.border}`,
                background: active ? `${col}18` : 'transparent',
                color: active ? col : c.textSec,
                fontSize: '12px', fontWeight: '600', fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              <span style={{ width: '10px', height: '3px', background: active ? col : c.border, borderRadius: '2px', display: 'inline-block' }} />
              {label}
              <span style={{ fontSize: '10px', opacity: 0.7 }}>{unit}</span>
            </button>
          );
        })}
      </div>

      {/* Graphique */}
      <div style={{ position: 'relative', height: '340px' }} onClick={handleChartClick}>
        <Line
          ref={chartRef}
          data={data}
          options={options}
        />
        {quickAnnot && onQuickAnnotate && (
          <div style={{
            position: 'absolute', left: quickAnnot.x, top: quickAnnot.y,
            transform: 'translateX(-50%)',
            background: darkMode ? '#1E1E32' : '#fff',
            border: '1.5px solid #1B77F1', borderRadius: '8px', padding: '8px 12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10,
            whiteSpace: 'nowrap', pointerEvents: 'all'
          }}>
            <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>
              {formatDateLocale(quickAnnot.timestamp, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={(e) => { e.stopPropagation(); onQuickAnnotate(quickAnnot.timestamp); setQuickAnnot(null); }}
                style={{ padding: '4px 10px', borderRadius: '6px', background: '#1B77F1', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                {t('session.annotations.quickAdd')}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setQuickAnnot(null); }}
                aria-label={t('common.close')}
                style={{ padding: '4px 8px', borderRadius: '6px', background: 'none',
                  border: '1px solid #e0e0e0', cursor: 'pointer', fontSize: '12px', color: '#6B7280' }}>
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Légende seuils */}
      <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {thresholdLines.map(({ label, color }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: c.textSec }}>
            <span style={{ width: '18px', height: '1px', borderTop: `2px dashed ${color}`, display: 'inline-block' }} />
            {label}
          </span>
        ))}
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: c.textSec, margin: '8px 0 0', fontStyle: 'italic', lineHeight: '1.5' }}>
        {t('charts.combined.footnote')}
        {withCo2 && ` ${t('charts.combined.footnoteCo2')}`}
      </p>
    </div>
  );
}

export default React.memo(CombinedChart, (prev, next) =>
  prev.readings === next.readings &&
  prev.darkMode === next.darkMode &&
  prev.onAnalyzeZoom === next.onAnalyzeZoom
);
